import { useEffect, useMemo, useRef, useState } from 'react';

import { useFormContext } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import {
  BootResponse,
  EPopupSubType,
  OfferAPIResponse,
  Preview
} from '@appcharge/shared-ui';
import useFeatureFlags from 'api/useFeatureFlags';
import useImages from 'api/useImages';
import useOffers from 'api/useOffers';
import useOfferDesign from 'api/useOfferDesign';
import { useProduct } from 'api/useProduct';
import useSegments from 'api/useSegments';
import useTabs from 'api/useTabs';
import useTheme from 'api/useTheme';
import { Offer, OfferBadge } from 'common/contracts';
import { EFeatureFlag, OfferType } from 'constants/enums';
import { useTemplateStudioState } from 'state/hooks/template-studio.hook';
import { ETemplateStudioSidebarBlock } from 'state/template-studio.state.service';
import { EToasterStatus, showToast } from 'utils/showToast';
import { TRANSLATE_MOCK_MAP } from 'views/Offers/Offer.consts';
import { OfferDesignTypes } from 'views/OfferDesign/OfferDesign.types';
import { z } from 'zod';

import { Product } from 'components/ProductsForm/ProductsForm.types';
import { bundleDesignsById } from 'components/TemplatesStudio/DesignsMetadata/bundles.metadata';
import { buildPreviewStoreBlocks } from 'components/TemplatesStudio/storeBlocks/storeBlocks.utils';

import { parsePersonalizationJson } from '../TestPersonalization/defaults';
import { templateStudioSchema } from '../validation-schema';

import { normalizeBodyBlocksV2ForPreview } from './usePreview.utils';
import { generateBootInfo } from './utils/boot';
import {
  buildEnrichedOffersPayload,
  MediaAssetOverrides,
  mergePersonalizationWithOffers
} from './utils/enrichedOffers';
import { generateOffersAPIResponse } from './utils/offers';
import { generatePlayerData } from './utils/playerData';
import {
  mapPreviewResponseToOfferAPIResponse,
  mergePreviewStoreThemeIntoBoot
} from './utils/previewResponse';

const PREVIEW_RESPONSE_STORAGE_KEY_PREFIX =
  'test_personalization_preview_response_';

const parsePreviewResponse = (
  stored: string
): Record<string, unknown> | null => {
  try {
    const parsed = JSON.parse(stored) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
};

export const usePreview = () => {
  const form = useFormContext<z.infer<typeof templateStudioSchema>>();
  const {
    previewDeviceLayout,
    displayPopupPreview,
    setDisplayPopupPreview,
    scrollToSection,
    personalizationJson,
    setGeneratedOffersJson,
    previewOffersResponse,
    setPreviewOffersResponse,
    openSecondarySidebarBlock
  } = useTemplateStudioState();
  const { watch } = form;
  const { publisherId } = useParams();
  const { getStoreTheme } = useTheme(publisherId);
  const { getImages } = useImages(publisherId);
  const { getProducts } = useProduct(publisherId!);
  const { getTabs } = useTabs(publisherId);
  const { featureFlags } = useFeatureFlags({ publisherId });
  const { getOfferDesigns } = useOfferDesign(publisherId!);
  const { getSegments } = useSegments(publisherId);
  const navigate = useNavigate();

  useEffect(() => {
    if (!publisherId || previewOffersResponse !== undefined) return;
    const key = `${PREVIEW_RESPONSE_STORAGE_KEY_PREFIX}${publisherId}`;
    const stored = sessionStorage.getItem(key);
    if (!stored) return;
    const parsed = parsePreviewResponse(stored);
    if (parsed) {
      setPreviewOffersResponse(parsed);
    } else {
      sessionStorage.removeItem(key);
    }
  }, [publisherId, previewOffersResponse, setPreviewOffersResponse]);

  const { getOffers: getPromotions } = useOffers({
    offerType: OfferType.SPECIAL_OFFER,
    fetchOnlyActiveOffers: true,
    shouldIgnorePagination: true
  });

  const { fetchAllOffers } = useOffers({});

  const [offersMap, setOffersMap] = useState<{
    bundles: Offer[];
    promotions: Offer[];
    progressBars: Offer[];
    rolling: Offer[];
    onePlusMany: Offer[];
    popups: Offer[];
  } | null>(null);

  const [offersLoadAttempted, setOffersLoadAttempted] = useState(false);
  const offersLoadedForPublisherRef = useRef<string | null>(null);

  useEffect(() => {
    if (!publisherId) {
      offersLoadedForPublisherRef.current = null;
      return;
    }
    if (offersLoadedForPublisherRef.current === publisherId) return;
    offersLoadedForPublisherRef.current = publisherId;
    setOffersMap(null);
    setOffersLoadAttempted(false);
    const loadOffers = async () => {
      try {
        const [
          bundleOffers,
          specialOffers,
          progressBarOffers,
          rollingOffers,
          buyOneGetManyOffers,
          popupOffers
        ] = await Promise.all([
          fetchAllOffers({
            offerType: OfferType.BUNDLE,
            fetchOnlyActiveOffers: true
          }),
          fetchAllOffers({
            offerType: OfferType.SPECIAL_OFFER,
            fetchOnlyActiveOffers: true
          }),
          fetchAllOffers({
            offerType: OfferType.PROGRESS_BAR,
            fetchOnlyActiveOffers: true
          }),
          fetchAllOffers({
            offerType: OfferType.ROLLING_OFFER,
            fetchOnlyActiveOffers: true
          }),
          fetchAllOffers({
            offerType: OfferType.BUY_ONE_GET_MANY,
            fetchOnlyActiveOffers: true
          }),
          fetchAllOffers({
            offerType: OfferType.POPUP,
            fetchOnlyActiveOffers: true
          })
        ]);
        setOffersMap({
          bundles: bundleOffers,
          promotions: specialOffers,
          progressBars: progressBarOffers,
          rolling: rollingOffers,
          onePlusMany: buyOneGetManyOffers,
          popups: popupOffers
        });
      } catch (error) {
        console.error('Failed to load offers for personalization:', error);
        setOffersMap(null);
      } finally {
        setOffersLoadAttempted(true);
      }
    };
    loadOffers();
  }, [publisherId, fetchAllOffers]);

  const formValues = watch();
  const formValuesRef = useRef(formValues);
  formValuesRef.current = formValues;

  const [formVersion, setFormVersion] = useState(0);
  const formValuesSignatureRef = useRef<string>('');

  useEffect(() => {
    const signature = JSON.stringify(formValues);
    if (signature !== formValuesSignatureRef.current) {
      formValuesSignatureRef.current = signature;
      setFormVersion((v) => v + 1);
    }
  }, [formValues]);

  const boot: BootResponse = useMemo(() => {
    if (!getStoreTheme.data) return;

    return generateBootInfo(
      formValuesRef.current,
      getStoreTheme.data,
      previewDeviceLayout,
      featureFlags
    );
  }, [formVersion, getStoreTheme.data, previewDeviceLayout, featureFlags]);

  const maxBundlePreviewProducts = useMemo(() => {
    const design = bundleDesignsById[formValues.bundle.selectedDesignId];
    if (!design) return 0;

    return design.maxPreviewProducts;
  }, [formValues.bundle.selectedDesignId]);

  const defaultOffers = useMemo(() => {
    if (!boot || !getProducts.data?.result || !getOfferDesigns.data?.result) return;

    const promotionBadges: OfferBadge[] =
      getPromotions?.data?.offers?.flatMap(
        (offer: Offer) => offer.badges || []
      ) || [];

    if (getProducts.data.result.length === 0) {
      navigate('../');
      showToast({
        message: 'Please add at least one product to the dashboard',
        status: EToasterStatus.ERROR
      });
      return;
    }

    if (getOfferDesigns.data?.result.length === 0) {
      navigate('../');
      showToast({
        message: 'Please add offer designs to the dashboard',
        status: EToasterStatus.ERROR
      });
      return;
    }

    const products: Product[] = [...getProducts.data?.result];

    if (products.length > 0 && products.length < 10) {
      const lastProduct = products[products.length - 1];
      while (products.length < 10) {
        products.push({ ...lastProduct });
      }
    }

    return generateOffersAPIResponse({
      formValues: formValuesRef.current,
      boot,
      products: products,
      maxBundlePreviewProducts: maxBundlePreviewProducts,
      offerUIS: getOfferDesigns.data?.result,
      promotionBadges,
      tabs: getTabs.data || [],
      navigate,
      onePlusManyOffers: offersMap?.onePlusMany,
      offersLoadAttempted
    });
  }, [
    formVersion,
    boot,
    getProducts.data?.result,
    getOfferDesigns.data?.result,
    getPromotions?.data?.offers,
    maxBundlePreviewProducts,
    getTabs.data,
    offersMap,
    offersLoadAttempted
  ]);

  const offersForPersonalization: OfferAPIResponse | undefined = useMemo(() => {
    if (!defaultOffers) return;

    const segments = Array.isArray(getSegments?.data) ? getSegments.data : [];
    const base = { ...defaultOffers, segments };
    if (!offersMap) return base;

    return {
      ...base,
      offers: {
        ...defaultOffers.offers,
        bundles: offersMap.bundles,
        promotions: offersMap.promotions,
        progressBars: offersMap.progressBars,
        rolling: offersMap.rolling,
        onePlusMany: offersMap.onePlusMany,
        popups: offersMap.popups
      }
    };
  }, [defaultOffers, offersMap, getSegments?.data]);

  const lastGeneratedJsonRef = useRef<string>('');

  useEffect(() => {
    if (!offersForPersonalization || !boot || !offersLoadAttempted) return;

    const assets = Array.isArray(getImages.data) ? getImages.data : [];
    const findAssetName = (url: string) =>
      assets
        .find(
          (a: { url?: string; name?: string }) => a?.url?.trim() === url?.trim()
        )
        ?.name?.trim() ?? '';

    const general = boot.storeTheme?.general;
    const logoUrl = general?.logo ?? '';
    const bannerUrl = boot.storeTheme?.storeScreen?.defaultBanner?.trim() ?? '';
    const preview = previewOffersResponse as
      | { profileFrameImg?: string; playerLevelImg?: string }
      | undefined;
    const generated = offersForPersonalization as
      | { profileFrameImg?: string; playerLevelImg?: string }
      | undefined;
    const profileFrameImgUrl =
      preview?.profileFrameImg?.trim() ??
      generated?.profileFrameImg?.trim() ??
      '';
    const playerLevelImgUrl =
      preview?.playerLevelImg?.trim() ??
      generated?.playerLevelImg?.trim() ??
      '';
    const bgMobileUrl = general?.backgroundImageMobile?.trim() ?? '';
    const bgDesktopUrl = general?.backgroundImageDesktop?.trim() ?? '';

    const mediaOverrides: MediaAssetOverrides = {};
    const addIfFound = (url: string, key: keyof MediaAssetOverrides) => {
      const name = findAssetName(url);
      if (url && name) mediaOverrides[key] = name;
    };
    addIfFound(logoUrl, 'logo');
    addIfFound(bannerUrl, 'bannerExternalId');
    addIfFound(profileFrameImgUrl, 'profileFrameImg');
    addIfFound(playerLevelImgUrl, 'playerLevelImg');
    addIfFound(bgMobileUrl, 'bgImageMobile');
    addIfFound(bgDesktopUrl, 'bgImageDesktop');

    const hasOverrides = Object.keys(mediaOverrides).length > 0;

    const enrichedOffers = buildEnrichedOffersPayload(
      boot,
      offersForPersonalization,
      previewOffersResponse ?? undefined,
      hasOverrides ? mediaOverrides : undefined
    );
    const nextJson = JSON.stringify(enrichedOffers, null, 2);
    if (nextJson !== lastGeneratedJsonRef.current) {
      lastGeneratedJsonRef.current = nextJson;
      setGeneratedOffersJson(nextJson);
    }
  }, [
    offersForPersonalization,
    setGeneratedOffersJson,
    boot,
    offersLoadAttempted,
    previewOffersResponse,
    getImages.data
  ]);

  const offers: OfferAPIResponse | undefined = useMemo(() => {
    let result: OfferAPIResponse | undefined;
    const isFromApiPreview = !!previewOffersResponse;
    if (previewOffersResponse) {
      result = mapPreviewResponseToOfferAPIResponse(previewOffersResponse);
    } else {
      const parsedPersonalization =
        parsePersonalizationJson(personalizationJson);
      if (parsedPersonalization && defaultOffers) {
        result = mergePersonalizationWithOffers(
          parsedPersonalization,
          defaultOffers
        );
      } else {
        result = defaultOffers;
      }
    }
    if (result && !isFromApiPreview) {
      result = { ...result, tabs: getTabs.data ?? [] };
    }
    const isPersonalizationPreview =
      !!previewOffersResponse ||
      !!(personalizationJson && parsePersonalizationJson(personalizationJson));
    const formProgressBarDesignId =
      boot?.storeTheme?.general?.progressBarInternalViewModel;
    const offerUIs = getOfferDesigns.data?.result ?? [];
    const formProgressBarOfferUI = formProgressBarDesignId
      ? offerUIs.find(
          (ui: any) =>
            ui.offerUiType === OfferDesignTypes.ProgressBar &&
            ui.externalId === formProgressBarDesignId
        ) ??
        offerUIs.find(
          (ui: any) => ui.offerUiType === OfferDesignTypes.ProgressBar
        )
      : null;

    if (
      isPersonalizationPreview &&
      !isFromApiPreview &&
      result?.offers?.progressBars?.length &&
      formProgressBarDesignId &&
      formProgressBarOfferUI
    ) {
      result = {
        ...result,
        offers: {
          ...result.offers,
          progressBars: result.offers.progressBars.map((pb: any) => {
            const offerUiId = formProgressBarDesignId;
            const offerUi = {
              ...formProgressBarOfferUI,
              offerUiId,
              externalId: formProgressBarDesignId
            };
            return {
              ...pb,
              offerUiId,
              offerUi
            };
          })
        }
      };
    }

    const formPromotionDesignId =
      boot?.storeTheme?.general?.specialOffersInternalViewModel;
    const formPromotionOfferUI = formPromotionDesignId
      ? offerUIs.find(
          (ui: any) =>
            ui.offerUiType === OfferDesignTypes.SpecialOffer &&
            ui.externalId === formPromotionDesignId
        ) ??
        offerUIs.find(
          (ui: any) => ui.offerUiType === OfferDesignTypes.SpecialOffer
        )
      : null;

    if (
      isPersonalizationPreview &&
      !isFromApiPreview &&
      result?.offers?.promotions?.length &&
      formPromotionDesignId
    ) {
      result = {
        ...result,
        offers: {
          ...result.offers,
          promotions: result.offers.promotions.map(
            (promo: any, index: number) => {
              const offerUiId = formPromotionDesignId;
              const offerUi = formPromotionOfferUI
                ? {
                    ...formPromotionOfferUI,
                    offerUiId,
                    externalId: formPromotionDesignId
                  }
                : promo.offerUi
                  ? {
                      ...promo.offerUi,
                      offerUiId,
                      externalId: formPromotionDesignId
                    }
                  : { offerUiId, externalId: formPromotionDesignId };
              const badgesFromGenerated =
                defaultOffers?.offers?.promotions?.[index]?.badges;
              const badges = Array.isArray(badgesFromGenerated)
                ? badgesFromGenerated
                : promo.badges ?? [];
              return {
                ...promo,
                offerUiId,
                offerUi,
                badges
              };
            }
          )
        }
      };
    }

    if (result && boot?.storeTheme?.general && !isFromApiPreview) {
      const general = boot.storeTheme.general;
      const formLogo =
        general.logo != null && String(general.logo).trim() !== ''
          ? general.logo
          : undefined;
      result = {
        ...result,
        logo: formLogo ?? result.logo
      } as OfferAPIResponse;
      if (typeof (result as any).storeTheme === 'object') {
        (result as any).storeTheme = {
          ...(result as any).storeTheme,
          logo: general.logo,
          bgImageMobile: general.backgroundImageMobile,
          bgImageDesktop: general.backgroundImageDesktop
        };
      }
    }

    return result;
  }, [
    defaultOffers,
    personalizationJson,
    previewOffersResponse,
    getTabs.data,
    boot,
    getOfferDesigns.data?.result
  ]);

  const bootForPreview: BootResponse | undefined = useMemo(
    () =>
      boot && previewOffersResponse
        ? mergePreviewStoreThemeIntoBoot(boot, previewOffersResponse)
        : boot,
    [boot, previewOffersResponse]
  );

  const shouldSendStoreBlocks =
    !!featureFlags?.[EFeatureFlag.DASHBOARD_SEND_STOREBLOCKS_TEMPLATE_STUDIO];

  const storeBlocks = useMemo(
    () =>
      shouldSendStoreBlocks
        ? buildPreviewStoreBlocks(formValuesRef.current)
        : [],
    [formVersion, shouldSendStoreBlocks]
  );

  const templateData = useMemo(
    () => ({
      promotion: {
        isCarouselView: watch('promotion.isCarouselView') || false,
        slidesPerViewDesktop: watch('promotion.slidesPerViewDesktop') || 3
      },
      brandKit: {
        customArrowImage: watch('brandKit.customArrowImage') || ''
      },
      triggeredPopups: {
        selectedDesignId: watch('triggeredPopups.selectedDesignId') || '',
        selectedBadgeDesignId:
          watch('triggeredPopups.selectedBadgeDesignId') || ''
      },
      rewardCalendar: {
        selectedDesignId: watch('rewardCalendar.selectedDesignId') || '',
        selectedBadgeDesignId:
          watch('rewardCalendar.selectedBadgeDesignId') || ''
      },
      bodyBlocksOrderV2: normalizeBodyBlocksV2ForPreview(
        watch('bodyBlocksOrderV2') || []
      ),
      storeBlocks
    }),
    [
      watch('promotion.isCarouselView'),
      watch('promotion.slidesPerViewDesktop'),
      watch('brandKit.customArrowImage'),
      watch('triggeredPopups.selectedDesignId'),
      watch('triggeredPopups.selectedBadgeDesignId'),
      watch('rewardCalendar.selectedDesignId'),
      watch('rewardCalendar.selectedBadgeDesignId'),
      watch('bodyBlocksOrderV2'),
      storeBlocks
    ]
  );

  const displayPopupSubType =
    openSecondarySidebarBlock ===
    ETemplateStudioSidebarBlock.TRIGGERED_POPUPS_POPUP
      ? EPopupSubType.STORE_REFRESH
      : openSecondarySidebarBlock ===
          ETemplateStudioSidebarBlock.REWARD_CALENDAR_POPUP
        ? EPopupSubType.REWARD_CALENDAR
        : openSecondarySidebarBlock === ETemplateStudioSidebarBlock.POPUP
          ? EPopupSubType.DAILY_BONUS
          : undefined;

  const previewProps: React.ComponentProps<typeof Preview> = useMemo(
    () => ({
      boot: bootForPreview,
      templateData,
      playerData: generatePlayerData(),
      device: previewDeviceLayout,
      translate: (key: string) => TRANSLATE_MOCK_MAP[key] || key,
      offers,
      displayPopup: displayPopupPreview,
      displayPopupSubType,
      onClosePopup: () => setDisplayPopupPreview(false),
      scrollToSection
    }),
    [
      bootForPreview,
      templateData,
      offers,
      previewDeviceLayout,
      displayPopupPreview,
      displayPopupSubType,
      setDisplayPopupPreview,
      scrollToSection
    ]
  );

  return { previewProps };
};
